import React, { useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { colors } from '../../brand-colors';
import SpeechInput from '../../components/SpeechInput';

const brandButtonStyle = {
  background: `linear-gradient(180deg, ${colors.redDark} 0%, ${colors.greyDarkest} 100%)`,
  border: 'none',
  color: colors.greyLightest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
};

function ImportIP() {
  const navigate = useNavigate();

  // Step tracking
  const [step, setStep] = useState('input'); // input, analyzing, questions, saving, complete

  // Input state
  const [inputType, setInputType] = useState('url'); // url or paste
  const [urlInput, setUrlInput] = useState('');
  const [pasteInput, setPasteInput] = useState('');

  // Analysis state
  const [analysisResult, setAnalysisResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedFramework, setSavedFramework] = useState(null);

  // Speech input ref
  const answerBeforeSpeechRef = useRef('');

  const handleAnalyze = async () => {
    const content = inputType === 'url' ? urlInput : pasteInput;
    if (!content.trim()) {
      setError('Please provide a URL or paste your methodology content.');
      return;
    }

    setLoading(true);
    setError('');
    setStep('analyzing');

    try {
      const response = await fetch('/api/authority-engineer/ip/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input_type: inputType,
          content: content.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setAnalysisResult(data.analysis);
        if (data.questions && data.questions.length > 0) {
          setQuestions(data.questions);
          setStep('questions');
        } else {
          // No questions needed, go straight to save
          setStep('saving');
          await handleSave(data.analysis, {});
        }
      } else {
        setError(data.error || 'Failed to analyze content');
        setStep('input');
      }
    } catch (err) {
      setError('Failed to connect to server');
      setStep('input');
    }

    setLoading(false);
  };

  const handleAnswerSubmit = async () => {
    let currentAnswer = answers[currentQuestionIndex];
    if (!currentAnswer?.trim()) {
      setError('Please provide an answer');
      return;
    }

    // Check if answer contains a URL - fetch its content
    const urlMatch = currentAnswer.match(/https?:\/\/[^\s]+/);
    if (urlMatch) {
      setLoading(true);
      setError('');
      try {
        const response = await fetch('/api/authority-engineer/ip/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            input_type: 'url',
            content: urlMatch[0]
          })
        });
        const data = await response.json();
        if (data.success && data.analysis) {
          // Append fetched content summary to the answer
          const fetchedInfo = `\n\n[Content from ${urlMatch[0]}]:\n${data.analysis.description || ''}\nKey points: ${(data.analysis.key_steps || []).join(', ')}`;
          currentAnswer = currentAnswer + fetchedInfo;
          setAnswers(prev => ({ ...prev, [currentQuestionIndex]: currentAnswer }));
        }
      } catch (err) {
        // If fetch fails, just continue with the URL as text
        console.log('Could not fetch URL content:', err);
      }
      setLoading(false);
    }

    if (currentQuestionIndex < questions.length - 1) {
      // More questions
      setCurrentQuestionIndex(prev => prev + 1);
      setError('');
    } else {
      // All questions answered, save the framework
      setStep('saving');
      setLoading(true);
      await handleSave(analysisResult, answers);
    }
  };

  const handleSave = async (analysis, questionAnswers) => {
    try {
      const response = await fetch('/api/authority-engineer/ip/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis: analysis,
          answers: questionAnswers,
          source_url: inputType === 'url' ? urlInput : null,
          source_content: inputType === 'paste' ? pasteInput : null
        })
      });

      const data = await response.json();

      if (data.success) {
        setSavedFramework(data.framework);
        setStep('complete');
      } else {
        setError(data.error || 'Failed to save framework');
        setStep('questions');
      }
    } catch (err) {
      setError('Failed to save framework');
      setStep('questions');
    }

    setLoading(false);
  };

  const renderInput = () => (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Import Existing IP</h5>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-4">
          Already have a methodology, framework, or process documented? Import it here and we'll
          analyze it, ask any clarifying questions, and add it to your IP library.
        </p>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        <Form.Group className="mb-4">
          <Form.Label><strong>How would you like to provide your IP?</strong></Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id="input-url"
              label="URL to a webpage"
              checked={inputType === 'url'}
              onChange={() => setInputType('url')}
            />
            <Form.Check
              type="radio"
              id="input-paste"
              label="Paste content directly"
              checked={inputType === 'paste'}
              onChange={() => setInputType('paste')}
            />
          </div>
        </Form.Group>

        {inputType === 'url' ? (
          <Form.Group className="mb-4">
            <Form.Label>URL to your methodology/framework page</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://example.com/my-methodology"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
            />
            <Form.Text className="text-muted">
              We'll fetch and analyze the content from this page.
            </Form.Text>
          </Form.Group>
        ) : (
          <Form.Group className="mb-4">
            <Form.Label>Paste your methodology content</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              placeholder="Paste your framework description, methodology steps, or process documentation here..."
              value={pasteInput}
              onChange={(e) => setPasteInput(e.target.value)}
            />
          </Form.Group>
        )}

        <div className="d-flex gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/command-center/authority-engineer')}
          >
            Cancel
          </Button>
          <Button
            style={brandButtonStyle}
            onClick={handleAnalyze}
            disabled={loading}
          >
            {loading ? <><Spinner size="sm" className="me-2" />Analyzing...</> : 'Analyze & Import'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderAnalyzing = () => (
    <Card className="shadow-sm">
      <Card.Body className="text-center py-5">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h5>Analyzing your methodology...</h5>
        <p className="text-muted">
          We're extracting the key elements and identifying any gaps that need clarification.
        </p>
        <ProgressBar animated now={60} className="mt-3" style={{ maxWidth: '300px', margin: '0 auto' }} />
      </Card.Body>
    </Card>
  );

  const renderQuestions = () => (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">A Few Clarifying Questions</h5>
          <small>{currentQuestionIndex + 1} of {questions.length}</small>
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        <ProgressBar
          now={((currentQuestionIndex + 1) / questions.length) * 100}
          className="mb-4"
          style={{ height: '8px' }}
        />

        <div className="mb-4">
          <h5>{questions[currentQuestionIndex]}</h5>
          <p className="text-muted small">
            This helps us fully capture your methodology for use throughout the platform.
          </p>
        </div>

        <Form.Group className="mb-4 position-relative">
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Your answer..."
            value={answers[currentQuestionIndex] || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [currentQuestionIndex]: e.target.value }))}
            style={{ paddingRight: '50px' }}
          />
          <SpeechInput
            onStart={() => {
              answerBeforeSpeechRef.current = answers[currentQuestionIndex] || '';
            }}
            onTranscript={(text) => {
              const prefix = answerBeforeSpeechRef.current ? answerBeforeSpeechRef.current + ' ' : '';
              setAnswers(prev => ({ ...prev, [currentQuestionIndex]: prefix + text }));
            }}
            onError={(msg) => setError(msg)}
            className="position-absolute"
            style={{ right: '10px', top: '10px' }}
            size="sm"
          />
        </Form.Group>

        <div className="d-flex justify-content-between">
          <Button
            variant="secondary"
            onClick={() => {
              if (currentQuestionIndex > 0) {
                setCurrentQuestionIndex(prev => prev - 1);
              } else {
                setStep('input');
              }
            }}
          >
            Back
          </Button>
          <Button
            style={brandButtonStyle}
            onClick={handleAnswerSubmit}
            disabled={loading}
          >
            {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Save Framework'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderSaving = () => (
    <Card className="shadow-sm">
      <Card.Body className="text-center py-5">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h5>Saving your IP framework...</h5>
        <p className="text-muted">
          We're processing your methodology and adding it to your IP library.
        </p>
      </Card.Body>
    </Card>
  );

  const renderComplete = () => (
    <Card className="shadow-sm">
      <Card.Body className="text-center py-5">
        <div style={{ fontSize: '4rem' }} className="mb-3">&#10003;</div>
        <h4 className="text-success mb-3">Framework Imported Successfully!</h4>
        <p className="text-muted mb-4">
          <strong>{savedFramework?.name}</strong> has been added to your IP library.
        </p>
        <div className="d-flex gap-2 justify-content-center">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/command-center/authority-engineer')}
          >
            Back to Authority Engineer
          </Button>
          <Button
            style={brandButtonStyle}
            onClick={() => {
              // Reset and import another
              setStep('input');
              setUrlInput('');
              setPasteInput('');
              setAnalysisResult(null);
              setQuestions([]);
              setAnswers({});
              setCurrentQuestionIndex(0);
              setSavedFramework(null);
            }}
          >
            Import Another
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <div className="mb-4">
            <Button
              variant="link"
              className="p-0 text-muted"
              onClick={() => navigate('/command-center/authority-engineer')}
            >
              &larr; Back to Authority Engineer
            </Button>
          </div>

          {step === 'input' && renderInput()}
          {step === 'analyzing' && renderAnalyzing()}
          {step === 'questions' && renderQuestions()}
          {step === 'saving' && renderSaving()}
          {step === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default ImportIP;
